<template>
  <span>
    <!-- Sharing one document was a tick-the-box-then-find-the-toolbar errand while a folder
         row shared in one click. Same gate as that toolbar: the folder's share permission. -->
    <button
      v-if="canShare"
      class="action-circle"
      v-tooltip.top="'Share'"
      aria-label="Share document"
      @click.stop="$emit('share', document)"
    >
      <i class="fas fa-share-alt"></i>
    </button>

    <button
      class="action-circle"
      v-tooltip.top="canPreview ? 'Preview' : 'No preview available'"
      :aria-label="canPreview ? 'Preview' : 'No preview available'"
      :disabled="!canPreview || isViewing"
      @click.stop="$emit('view', document)"
    >
      <span class="spinner-border spinner-border-sm" v-if="isViewing"></span>
      <i v-else :class="canPreview ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
    </button>

    <button
      class="action-circle"
      v-tooltip.top="'Download'"
      aria-label="Download"
      :disabled="isDownloading"
      @click.stop="download"
    >
      <span
        class="spinner-border spinner-border-sm"
        v-if="isDownloading"
      ></span>
      <i v-else class="fas fa-download"></i>
    </button>

    <button
      v-if="canDelete"
      class="action-circle action-circle-danger"
      v-tooltip.top="'Delete'"
      aria-label="Delete"
      :disabled="isDeleting"
      @click.stop="confirmDelete"
    >
      <span class="spinner-border spinner-border-sm" v-if="isDeleting"></span>
      <i v-else class="fas fa-trash-alt"></i>
    </button>
  </span>
</template>

<script>
import Swal from "sweetalert2";
import { truncateMiddle } from "@/utils/fileHelpers.js";
import { mapActions } from "vuex";
import { confirmDialog, isViewable } from "./useDocumentsArea";

export default {
  name: "DocumentRowActions",
  props: {
    document: { type: Object, required: true },
    subjectUserId: { type: Number, default: null },
    canDelete: { type: Boolean, default: true },
    canShare: { type: Boolean, default: false },
    isViewing: { type: Boolean, default: false },
  },
  emits: ["changed", "share", "view"],
  data() {
    return {
      isDownloading: false,
      isDeleting: false,
    };
  },
  computed: {
    canPreview() {
      return isViewable(this.document);
    },
  },
  methods: {
    ...mapActions({
      downloadDocument: "folders/download",
      removeDocument: "folders/deleteDocument",
    }),

    withOwner() {
      return { ...this.document, owner_id: this.subjectUserId };
    },

    download() {
      if (this.isDownloading) {
        return;
      }
      this.isDownloading = true;

      this.downloadDocument(this.withOwner()).finally(() => {
        this.isDownloading = false;
      });
    },

    confirmDelete() {
      const dialog = confirmDialog(Swal);

      dialog
        .fire({
          title: `Delete "${truncateMiddle(
            this.document.original_file_name,
            40
          )}"?`,
          text: "This cannot be undone.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it",
          cancelButtonText: "No, cancel",
          reverseButtons: true,
        })
        .then((result) => {
          if (!result.isConfirmed) {
            return;
          }

          this.isDeleting = true;
          this.removeDocument(this.document.id)
            .then((deleted) => {
              if (!deleted) {
                // The store already reported why, and nothing changed to reload.
                return;
              }
              this.$emit("changed");
              dialog.fire(
                "Deleted!",
                "The document has been deleted.",
                "success"
              );
            })
            .finally(() => {
              this.isDeleting = false;
            });
        });
    },
  },
};
</script>
